import type { CSSProperties } from "react";
import { Link } from "react-router";

import { useURL } from "~/contexts";
import ChevronDown from "~/hec/assets/icons/chevron-down";
import { useChromePrefetch } from "~/hec/hooks/useChromePrefetch";
import type { ReadableColorPair } from "~/hec/utils/types";
import { cn } from "~/lib/ui";
import { categoryUrlBuilder } from "~/lib/utils/url";

interface CategoryLinkProps {
  categoryId: string;
  categoryName: string;
  /**
   * Full multi-segment slug for the category (e.g.
   * `personal-care/acne-and-skincare`). When omitted, the URL falls back to
   * just `categoryId` — only correct for top-level categories.
   */
  categoryPath?: string;
  altCategoryUrl?: string | null;
  hasSubCategories?: boolean;
  shopAll?: boolean;
  isOpen?: boolean;
  /**
   * COM-581: BM-driven row highlight (`c_navigationColor`, already validated
   * and paired with a readable text colour by `parseNavigationColor` in
   * `NavItem`). Painted inline because the colour is data, not a class; the
   * default hover/focus treatments are swapped for a slight darken so the BM
   * colour is kept on hover. `undefined` = today's default styling.
   */
  highlight?: ReadableColorPair;
}

export function CategoryLink({
  categoryId,
  categoryName,
  categoryPath,
  altCategoryUrl,
  hasSubCategories = false,
  shopAll = false,
  isOpen,
  highlight,
}: CategoryLinkProps) {
  const url = useURL();
  // "none" inside the Builder editor (non-nav edit target) so the nav doesn't
  // flood the editor with category `.data` prefetches; "intent" otherwise.
  const prefetch = useChromePrefetch("intent");
  const categoryUrl =
    altCategoryUrl ||
    categoryUrlBuilder({ id: categoryId, path: categoryPath });

  const highlighted = highlight !== undefined;
  // `undefined` (not `{}`) when not highlighted so the DOM carries no empty
  // `style=""` and the default rows stay byte-identical to before.
  const style: CSSProperties | undefined = highlight
    ? { backgroundColor: highlight.backgroundColor, color: highlight.textColor }
    : undefined;

  const className = cn(
    "flex w-full items-center justify-between py-[0.53125rem] pl-[1.25rem] pr-[0.5rem] font-grotesque text-sm text-blue-05 no-underline",
    {
      "hover:[text-shadow:_0_0_0.0625rem_#132744] focus:[text-shadow:_0_0_0.0625rem_#132744]":
        hasSubCategories && !highlighted,
      "[text-shadow:_0_0_0.0625rem_#132744]":
        isOpen && hasSubCategories && !highlighted,
      "hover:bg-accent hover:text-white hover:rounded-lg":
        !hasSubCategories && !highlighted,
      "hover:no-underline": !hasSubCategories,
      // Highlighted row: rounded at rest, BM colour KEPT on hover/focus and
      // darkened ~10%. `filter` is the one Tailwind-only hover that works on
      // an inline background (a `hover:bg-*` class cannot know the colour); a
      // navy text-shadow under white text would look dirty, so it is dropped.
      "rounded-lg transition-[filter] hover:brightness-90": highlighted,
      // Keyboard focus: the leaf <Link> takes focus itself; the parent row is a
      // non-focusable <span> inside NavItem's CollapsibleTrigger <button>, so it
      // darkens off that button's named group instead.
      "focus-visible:brightness-90": highlighted && !hasSubCategories,
      "group-focus-visible/nav-trigger:brightness-90":
        highlighted && hasSubCategories,
      "font-medium": shopAll,
    },
  );

  if (hasSubCategories) {
    return (
      <span
        className={className}
        style={style}
        data-testid={`nav-category-row-${categoryId}`}
      >
        {categoryName}
        {/* `currentColor` so the chevron follows the row's text colour (white
            on a dark BM background). The icon's own default is #142745, one
            unit off text-blue-05, so the default rows look the same. */}
        <ChevronDown
          fill="currentColor"
          className={cn("h-[7px] w-3 transition-transform", {
            "rotate-0": isOpen,
            "-rotate-90": !isOpen,
          })}
        />
      </span>
    );
  }

  return (
    <Link
      to={url(categoryUrl)}
      prefetch={prefetch}
      className={className}
      style={style}
      data-testid={
        shopAll
          ? `nav-category-shop-all-link-${categoryId}`
          : `nav-category-link-${categoryId}`
      }
    >
      {categoryName}
    </Link>
  );
}
